import { ReactComponent as DownArrow } from '../assets/down-arrow.svg';
import { ReactComponent as DwnldArrow } from '../assets/download-arrow.svg';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getEquipmentTranslation } from '../utils/equipmentTranslation';
import { CSVLink } from 'react-csv';
import { BulkUpdateEquipmentModal } from './BulkUpdateEquipmentModal';

const EquipmentTable = ({
  equipments,
  setDeleteModal,
  setUpdateModal,
  setChangeEquipment,
  loading,
  setAddEquipmentModal,
  setViewContentModal,
  setViewEquipment,
}) => {
  const [sortedEquipments, setSortedEquipments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    if (!equipments?.length) return;
    const sorted = [...equipments].sort(
      (a, b) => (b.availableInstances || 0) - (a.availableInstances || 0)
    );
    setSortedEquipments(sorted);
  }, [equipments]);

  // For searching the equipment by name
  const displayedEquipments = useMemo(
    () => sortedEquipments.filter((e) => e.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [sortedEquipments, searchTerm]
  );

  // Prepare download data with all available equipment information
  const downloadEquipments = useMemo(() => {
    if (!equipments.length) return [];

    return equipments.map((equipment) => ({
      'Equipment ID': equipment._id || equipment.id,
      'Name (English)': equipment.translations?.en?.name || equipment.name,
      'Name (Spanish)': equipment.translations?.es?.name || '',
      'Category (English)': equipment.translations?.en?.category || equipment.category,
      'Category (Spanish)': equipment.translations?.es?.category || '',
      'Description (English)': equipment.translations?.en?.description || equipment.description,
      'Description (Spanish)': equipment.translations?.es?.description || '',
      Quantity: equipment.qty,
      'Rental Price': equipment.rentalPrice,
      Location: equipment.location,
      Availability: equipment.availability ? 'YES' : 'NO',
      'Self Transport': equipment.selfTransport,
      Specs:
        typeof equipment.specs === 'string'
          ? equipment.specs
          : JSON.stringify(equipment.specs || ''),
      Tutorials:
        typeof equipment.tutorials === 'string'
          ? equipment.tutorials
          : JSON.stringify(equipment.tutorials || ''),
      Images: Array.isArray(equipment.images) ? equipment.images.join('; ') : '',
      'Created By': equipment.createdBy || '',
      'Created At': equipment.createdAt || '',
      'Updated At': equipment.updatedAt || '',
    }));
  }, [equipments]);

  const handleSort = (key, ord) => {
    setSortedEquipments((prev) => {
      const copy = [...prev];
      copy.sort((a, b) => {
        let cmp = 0;
        switch (key) {
          case 'name':
            cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            break;
          case 'qty':
            cmp = (a.availableInstances || 0) - (b.availableInstances || 0);
            break;
          case 'price':
            cmp = a.rentalPrice - b.rentalPrice;
            break;
          case 'loc':
            cmp = a.location.toLowerCase().localeCompare(b.location.toLowerCase());
            break;
        }
        return ord === 'asc' ? cmp : -cmp;
      });
      return copy;
    });
  };

  return (
    <>
      {bulkUpdateModal && <BulkUpdateEquipmentModal setBulkUpdateModal={setBulkUpdateModal} />}
      <div className="overflow-x-auto flex flex-col gap-5">
        <div className="flex justify-between items-center mt-5 px-5">
          <input
            type="text"
            placeholder="Search equipment by name..."
            className="border border-gray-300 rounded-md py-2 px-4 w-64 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex space-x-4">
            {equipments.length > 0 ? (
              <CSVLink
                className="bg-[#2E7A32] hover:bg-green-500 text-white font-bold py-2 px-4 rounded-full flex items-center space-x-2"
                data={downloadEquipments}
                target="_blank"
                filename={'Farmshare-equipment.csv'}
              >
                <DwnldArrow width={20} height={20} />
                <span>Download Equipment Information</span>
              </CSVLink>
            ) : (
              <button
                className="bg-gray-400 text-white font-bold py-2 px-4 rounded-full flex items-center space-x-2 cursor-not-allowed"
                disabled
                aria-disabled="true"
                title="No equipment available to download"
              >
                <DwnldArrow width={20} height={20} />
                <span>Download Equipment Information</span>
              </button>
            )}
            <button
              className="bg-[#2E7A32] hover:bg-green-500 text-white font-bold py-2 px-4 rounded-full flex items-center space-x-2"
              onClick={() => setBulkUpdateModal(true)}
            >
              <DwnldArrow width={20} height={20} style={{ transform: 'rotate(180deg)' }} />
              <span>Bulk Update</span>
            </button>
            <button
              className="bg-[#2E7A32] hover:bg-green-500 text-white font-bold py-1 px-3 rounded-full"
              onClick={() => setAddEquipmentModal(true)}
            >
              Add Equipment +
            </button>
          </div>
        </div>
        <table className="min-w-full table-auto border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
                <div className="flex items-center justify-between">
                  <span>Name</span>
                  <div className="flex space-x-2">
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('name', 'asc')}
                      className="hover:cursor-pointer"
                      style={{ transform: 'rotate(180deg)' }}
                    />
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('name', 'desc')}
                      className="hover:cursor-pointer"
                    />
                  </div>
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
                <div className="flex items-center justify-between">
                  <span>Quantity</span>
                  <div className="flex space-x-2">
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('qty', 'asc')}
                      className="hover:cursor-pointer"
                      style={{ transform: 'rotate(180deg)' }}
                    />
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('qty', 'desc')}
                      className="hover:cursor-pointer"
                    />
                  </div>
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
                <div className="flex items-center justify-between">
                  <span>Rental Price</span>
                  <div className="flex space-x-2">
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('price', 'asc')}
                      className="hover:cursor-pointer"
                      style={{ transform: 'rotate(180deg)' }}
                    />
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('price', 'desc')}
                      className="hover:cursor-pointer"
                    />
                  </div>
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
                <div className="flex items-center justify-between">
                  <span>Location</span>
                  <div className="flex space-x-2">
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('loc', 'asc')}
                      className="hover:cursor-pointer"
                      style={{ transform: 'rotate(180deg)' }}
                    />
                    <DownArrow
                      width={15}
                      height={15}
                      onClick={() => handleSort('loc', 'desc')}
                      className="hover:cursor-pointer"
                    />
                  </div>
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
                Options
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : (
              displayedEquipments.map((equip) => {
                const equipmentName = getEquipmentTranslation(equip, 'name', currentLang);
                const equipmentType = getEquipmentTranslation(equip, 'type', currentLang);

                return (
                  <tr
                    key={equip._id}
                    className="bg-white hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setViewContentModal(true);
                      setViewEquipment(equip);
                    }}
                  >
                    <td className="border border-gray-300 px-4 py-2 flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src={equip.images[0]}
                          alt={equipmentName}
                          className="w-24 h-24 object-cover"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="font-semibold">{equipmentName}</div>
                        <div className="text-gray-500 text-sm capitalize">
                          Type: {equipmentType}
                        </div>
                        <div className="text-sm text-gray-600">
                          {equip.totalInstances || equip.qty || 0}{' '}
                          {(equip.totalInstances || equip.qty) === 1 ? 'unit' : 'units'}
                        </div>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {equip.totalInstances || equip.qty || 0}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">${equip.rentalPrice}</td>
                    <td className="border border-gray-300 px-4 py-2">{equip.location}</td>
                    <td className="border border-gray-300 px-4 py-2 capitalize">
                      <div className="flex justify-around">
                        <button
                          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUpdateModal(true);
                            setViewEquipment(equip);
                          }}
                        >
                          Update
                        </button>
                        <button
                          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteModal(true);
                            setViewEquipment(equip);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export { EquipmentTable };
